export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const serviceType = config.public.serviceType
  const isHina = config.public.isHina

  // ----- domain/codeに該当する部分のデータ作成 -----
  const getSystemCode = (targetCode: Code): Code => {
    if (!targetCode.values) {
      return targetCode
    }
    let values = targetCode.values.filter((value) => {
      if (!value.system) {
        return true
      } else {
        return value.system.includes(serviceType)
      }
    })
    let result = clone(targetCode)
    result.values = values
    return result
  }

  const contentsAccess = Object.freeze(getSystemCode(CODE_CONTENTS_ACCESS))
  const codeCurationTopicCategory = Object.freeze(getSystemCode(CODE_CURATION_TOPIC_CATEGORY))
  const codeDbCategory = Object.freeze(getSystemCode(CODE_DB_CATEGORY))
  const codeDigitalContentsType = Object.freeze(getSystemCode(CODE_DIGITAL_CONTENTS_TYPE))
  const codeFacetRightsType = Object.freeze(getSystemCode(CODE_FACET_RIGHTS_TYPE))
  const codeItemApiType = Object.freeze(getSystemCode(CODE_ITEM_API_TYPE))
  const codeLanguage = Object.freeze(getSystemCode(CODE_LANGUAGE))
  const codeRdfType = Object.freeze(getSystemCode(CODE_RDF_TYPE))
  const codeRightsGroup = Object.freeze(getSystemCode(CODE_RIGHTS_GROUP))
  const codeRightsType = Object.freeze(getSystemCode(CODE_RIGHTS_TYPE))
  const codeSearchContentsType = Object.freeze(getSystemCode(CODE_SEARCH_CONTENTS_TYPE))

  const getCodeContentsAccess = (): Code => contentsAccess
  const getCodeCurationTopicCategory = (): Code => codeCurationTopicCategory
  const getCodeDbCategory = (): Code => codeDbCategory
  const getCodeDigitalContentsType = (): Code => codeDigitalContentsType
  const getCodeFacetRightsType = (): Code => codeFacetRightsType
  const getCodeItemApiType = (): Code => codeItemApiType
  const getCodeLanguage = (): Code => codeLanguage
  const getCodeRdfType = (): Code => codeRdfType
  const getCodeRightsGroup = (): Code => codeRightsGroup
  const getCodeRightsType = (): Code => codeRightsType
  const getCodeSearchContentsType = (): Code => codeSearchContentsType
  const getSystemCodes = (): Code[] => {
    return [
      getCodeContentsAccess(),
      getCodeCurationTopicCategory(),
      getCodeDbCategory(),
      getCodeDigitalContentsType(),
      getCodeFacetRightsType(),
      getCodeItemApiType(),
      getCodeLanguage(),
      getCodeRdfType(),
      getCodeRightsGroup(),
      getCodeRightsType(),
      getCodeSearchContentsType(),
    ]
  }

  // ----- コンテンツコードの作成 -----
  let contentsCodeBase = isHina ? [] : ['internet', 'restricted', 'thumb', 'iiif', 'description']
  let contentsCodeLangBase = isHina ? {} : {
    internet: { ja: 'ウェブ公開', en: 'Available on the web' },
    restricted: { ja: '限定公開', en: 'Access restricted' },
    thumb: { ja: 'サムネイル有', en: 'Has Thumbnails' },
    iiif: { ja: 'IIIF対応', en: 'IIIF ready' },
    description: { ja: '解説有', en: 'Description' },
  }
  getCodeDigitalContentsType().values.forEach(v => {
    if (v.code !== 'various') {
      contentsCodeBase.push(v.code)
      contentsCodeLangBase[v.code] = v.value
    }
  })
  const contentsCode = Object.freeze(contentsCodeBase)
  const contentsCodeLang = Object.freeze(contentsCodeLangBase)

  const getContentsCode = (): string[] => contentsCode as string[]
  const getContentsCodeLang = (): any => contentsCodeLang


  // ----- 共通ラベルの作成 -----
  const commonLabelDefConfig = {
    temporal: {
      system: {
        hina: {
          name: {
            ja: '時間',
            en: 'Time',
          }
        }
      }
    }
  }

  let commonLabelDefBase: CommonLabelDefinition[] = []
  COMMON_LABEL_DEF.forEach(v => {
    if (commonLabelDefConfig[v.id] && commonLabelDefConfig[v.id].system[serviceType]) {
      // 加工の必要あり
      let def = clone(v)
      let setting = commonLabelDefConfig[v.id].system[serviceType]
      if (Object.keys(setting).length > 0) {
        // 設定に細かいものが入っていれば設定を上書きする
        // 設定が空っぽだったら値自体をスキップする
        Object.keys(setting).forEach(key => {
          def[key] = setting[key]
        })
        commonLabelDefBase.push(def)
      }
    } else {
      commonLabelDefBase.push(clone(v))
    }
  })

  const commonLabelDef = Object.freeze(commonLabelDefBase)
  const getCommonLabelDef = (): CommonLabelDefinition[] => commonLabelDef as CommonLabelDefinition[]


  // ----- 共通検索関連の作成 -----
  const commonSearchField = Object.freeze(COMMON_SEARCH_FIELD.filter((v) => v.system.includes(serviceType)))
  const commonSearchColumn = Object.freeze(COMMON_SEARCH_COLUMN.filter((v) => v.system.includes(serviceType)))

  const getCommonSearchField = (): CustomSearchFieldEx[] => commonSearchField as CustomSearchFieldEx[]
  const getCommonSearchColumn = (): CustomSearchColumnEx[] => commonSearchColumn as CustomSearchColumnEx[]


  // ----- テーマの作成 -----
  const curationThemes = Object.freeze([
    {
      id: 'default',
      name: { ja: 'デフォルト', en: 'Default' },
      description: {
        ja: `${isHina ? 'ひなぎく' : 'ジャパンサーチ'}の標準のスタイルです`,
        en: `Default style of ${isHina ? 'HINAGIKU' : 'Japan Search'}`,
      },
    },
    {
      id: 'modern',
      name: { ja: 'モダン', en: 'Modern' },
      description: {
        ja: 'モダンスタイル',
        en: 'Modern',
      },
    },
    {
      id: 'wa-modern',
      name: { ja: '和モダン', en: 'Japanese Modern' },
      description: {
        ja: '和モダンスタイル',
        en: 'Japanese Modern Style',
      },
    },
  ])
  const getCurationThemes = (): any[] => curationThemes as any[]


  // ----- ソートの作成 -----
  const sortOptions: SortOption[] = Object.freeze(isHina ? [
    {
      name: { ja: '時間（古い順）', en: 'Time(Old)' },
      index: 'sindex.sortTemporal',
      asccend: true,
    },
    {
      name: { ja: '時間（新しい順）', en: 'Time(New)' },
      index: 'sindex.sortTemporal',
      asccend: false,
    },
    {
      name: { ja: '人物/団体', en: 'Contributor' },
      index: 'sindex.sortContributor',
      asccend: true,
    },
    {
      name: { ja: 'タイトル', en: 'Title' },
      index: 'sindex.sortTitle',
      asccend: true,
    },
  ] : [
    {
      name: { ja: 'タイトル', en: 'Title' },
      index: 'sindex.sortTitle',
      asccend: true,
    },
    {
      name: { ja: '人物/団体', en: 'Contributor' },
      index: 'sindex.sortContributor',
      asccend: true,
    },
    {
      name: { ja: '時間/時代（古い順）', en: 'Time(Old)' },
      index: 'sindex.sortTemporal',
      asccend: true,
    },
    {
      name: { ja: '時間/時代（新しい順）', en: 'Time(New)' },
      index: 'sindex.sortTemporal',
      asccend: false,
    },
    {
      name: { ja: 'クエリ関連度順', en: 'Query Relevance' },
      index: 'original',
    },
  ]) as SortOption[]
  const getSortOptions = (): SortOption[] => sortOptions


  // ----- バリデーションルール用の書き換え -----
  const validationRuleEnumRewrite = isHina ? {
    'jps-search_field_type': {
      base: clone(SEARCH_FIELD_TYPE_DEF),
      removeCode: [ 'permission', 'owner_org' ],
    },
  } : {
    'jps-search_field_type': {
      base: clone(SEARCH_FIELD_TYPE_DEF),
      removeCode: [ 'prefectures', 'temporal_group', 'permission', 'owner_org' ],
    },
  }
  let validationRuleEnumBase = {}
  Object.entries(validationRuleEnumRewrite).forEach(([key, value]) => {
    let base = value['base']
    if (value['removeCode']) {
      value['removeCode'].forEach(removeKey => {
        delete base['c'][removeKey]
      })
    }
    validationRuleEnumBase[key] = Object.freeze(base)
  })
  const getValidationRuleEnum = (): any => validationRuleEnumBase


  return {
    provide: {
      // domain/code
      getCodeContentsAccess,
      getCodeCurationTopicCategory,
      getCodeDbCategory,
      getCodeDigitalContentsType,
      getCodeFacetRightsType,
      getCodeItemApiType,
      getCodeLanguage,
      getCodeRdfType,
      getCodeRightsGroup,
      getCodeRightsType,
      getCodeSearchContentsType,
      getSystemCodes,
      // コンテンツコード
      getContentsCode,
      getContentsCodeLang,
      // 共通ラベル
      getCommonLabelDef,
      // 共通検索フィールド
      getCommonSearchField,
      getCommonSearchColumn,
      // テーマ
      getCurationThemes,
      // ソート
      getSortOptions,
      // バリデーションルール
      getValidationRuleEnum,
    },
  }

})